'use client';

import { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import HeaderMenuImage from '@/components/sections/builder/elements/header/header/header-menu-image';
import {
  HeaderElementDataType,
  HeaderMenuElementDataType,
} from '@/components/sections/builder/elements/header/header/type';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { IMenuItem } from '@/types/navigation';
import { ChevronDownIcon } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { HeaderSubMenuType } from '@/components/sections/builder/settings/header-sub-menu-setting';

export default function HeaderMenuContent({
  menu,
  isMegaMenu,
  parentData,
  preview,
  openMenuId,
  setOpenMenuId,
  openSubMenuId,
  setOpenSubMenuId,
}: {
  menu: IMenuItem;
  isMegaMenu: boolean;
  parentData?: HeaderElementDataType;
  preview?: boolean;
  openMenuId?: string;
  setOpenMenuId?: Dispatch<SetStateAction<string>>;
  openSubMenuId?: string;
  setOpenSubMenuId?: Dispatch<SetStateAction<string>>;
}) {
  const query = useSearchParams();
  const themeId: string = query.get('theme_id') || '';
  const token = query.get('token') || '';

  const [isSticky, setIsSticky] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const flattenChildrenToSubMenus = (
    children: IMenuItem[] | undefined,
    subMenus: HeaderSubMenuType[] = [],
    groupIndex: number = 0
  ): HeaderSubMenuType[] => {
    if (!children) return subMenus;
    for (const child of children) {
      subMenus.push({
        id: child.id,
        label: child.title,
        link: child.url_path,
        groupIndex,
        groupLabel: undefined,
      });

      if (child.children?.length) {
        flattenChildrenToSubMenus(child.children, subMenus, groupIndex);
      }
    }
    return subMenus;
  };

  const transformMenuToHeaderData = (
    menu: IMenuItem,
    isMegaMenu = false
  ): HeaderMenuElementDataType => {
    const hasChildren = menu.children && menu.children.length > 0;

    return {
      name: menu.title,
      label: menu.title,
      link: menu.url_path || undefined,
      subMenuType: !hasChildren ? 'NONE' : isMegaMenu ? 'MEGA_MENU' : 'DROPDOWN',
      dropdownWidth: 'AUTO',
      megaMenuWidth: 'FULL_WIDTH',
      subMenus: hasChildren ? flattenChildrenToSubMenus(menu.children) : [],
    };
  };

  const data = transformMenuToHeaderData(menu, isMegaMenu);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        data.subMenuType === 'MEGA_MENU'
      ) {
        setOpenMenuId?.('');
        setOpenSubMenuId?.('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {/* Mobile components */}
      <Accordion type='single' collapsible className='block w-full lg:hidden'>
        <AccordionItem value='item-1' className='w-full'>
          <AccordionTrigger
            className={cn(
              'w-full flex-row-reverse justify-between text-base  font-normal [&>svg]:h-5 [&>svg]:w-5',
              data.subMenuType === 'NONE' && '[&>svg]:opacity-0'
            )}
          >
            {data.subMenuType === 'NONE' ? (
              <a
                href={
                  data.link?.includes('http')
                    ? data.link
                    : `/${data.link}${
                        token
                          ? `?theme_id=${themeId}&token=${token}`
                          : preview
                          ? `?theme_id=${themeId}&isPreview=true`
                          : ''
                      }`
                }
              >
                {data.label}
              </a>
            ) : (
              data.label
            )}
          </AccordionTrigger>
          <AccordionContent className='text-left'>
            {data.subMenuType === 'DROPDOWN' && (
              <ul className='py-2 text-base text-gray-700 dark:text-gray-400'>
                {menu.children?.map((subMenu) => (
                  <li key={subMenu.id}>
                    <a
                      href={
                        subMenu.url_path.includes('http')
                          ? subMenu.url_path
                          : `/${subMenu.url_path}${
                              token
                                ? `?theme_id=${themeId}&token=${token}`
                                : preview
                                ? `?theme_id=${themeId}&isPreview=true`
                                : ''
                            }`
                      }
                      className='block rounded-sm px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'
                    >
                      {subMenu.title}
                    </a>
                    {subMenu.children?.length > 0 && (
                      <button
                        onClick={() =>
                          setOpenSubMenuId?.(openSubMenuId === subMenu.id ? '' : subMenu.id)
                        }
                      ></button>
                    )}
                    {openSubMenuId === subMenu.id && subMenu.children?.length > 0 && (
                      <ul className='pl-4'>
                        {subMenu.children.map((child) => (
                          <li key={child.id}>
                            <a
                              href={
                                child.url_path.includes('http')
                                  ? child.url_path
                                  : `/${child.url_path}${
                                      token
                                        ? `?theme_id=${themeId}&token=${token}`
                                        : preview
                                        ? `?theme_id=${themeId}&isPreview=true`
                                        : ''
                                    }`
                              }
                              className='block rounded-sm px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'
                            >
                              {child.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {data.subMenuType === 'MEGA_MENU' && (
              <div className='flex w-full flex-col justify-between gap-4 px-3 py-2'>
                {menu.children?.map((menuItem) => (
                  <div key={menuItem.id}>
                    {menuItem.title && (
                      <a
                        href={
                          menuItem.url_path.includes('http')
                            ? menuItem.url_path
                            : `/${menuItem.url_path}${
                                token
                                  ? `?theme_id=${themeId}&token=${token}`
                                  : preview
                                  ? `?theme_id=${themeId}&isPreview=true`
                                  : ''
                              }`
                        }
                        className={cn(
                          'mb-1 text-base',
                          menuItem.children.length
                            ? 'font-semibold text-black'
                            : 'rounded-lg p-2 text-sm font-normal text-[#52535B] hover:bg-gray-50 dark:hover:bg-gray-700'
                        )}
                      >
                        {menuItem.title}
                      </a>
                    )}
                    <ul>
                      {menuItem.children?.map((subMenu) => (
                        <li key={subMenu.id}>
                          <a
                            href={
                              subMenu.url_path?.includes('http')
                                ? subMenu.url_path
                                : `/${subMenu.url_path}${
                                    token
                                      ? `?theme_id=${themeId}&token=${token}`
                                      : preview
                                      ? `?theme_id=${themeId}&isPreview=true`
                                      : ''
                                  }`
                            }
                            className='flex gap-x-2 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700'
                          >
                            <div>
                              <div>{subMenu.title}</div>
                            </div>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div className='grid grid-cols-1 gap-2'>
                  {menu.images?.slice(0, 3).map((image, index) => (
                    <div key={index}>
                      <HeaderMenuImage
                        title={image.title}
                        src={image.image_url}
                        parentData={parentData}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Desktop components */}
      <div
        ref={dropdownRef}
        className={cn('inline-block', isSticky && 'z-50 w-full transition-all duration-300')}
      >
        <div
          className={cn(
            'hidden flex-shrink-0 cursor-pointer items-center gap-x-2 text-base lg:flex',
            menu.is_highlight && 'rounded bg-red-500 px-2 py-1 text-white'
          )}
          onMouseEnter={() => {
            if (data.subMenuType === 'NONE') {
              return;
            } else if (data.subMenuType === 'MEGA_MENU') {
              setOpenMenuId?.(menu.id);
            }
          }}
          onClick={() => {
            if (data.subMenuType === 'DROPDOWN') {
              if (openMenuId === menu.id) {
                setOpenMenuId?.('');
              } else {
                setOpenMenuId?.(menu.id);
              }
            }
          }}
        >
          {data.subMenuType === 'NONE' ? (
            <Link
              className='flex-shrink-0'
              href={
                data.link?.includes('http')
                  ? data.link
                  : `/${data.link}${
                      token
                        ? `?theme_id=${themeId}&token=${token}`
                        : preview
                        ? `?theme_id=${themeId}&isPreview=true`
                        : ''
                    }`
              }
            >
              {data.label}
            </Link>
          ) : (
            <span>{data.label}</span>
          )}
          {data.subMenuType !== 'NONE' && <ChevronDownIcon className='h-4 w-4' />}
        </div>
        {openMenuId === menu.id && data.subMenuType === 'DROPDOWN' && (
          <div
            className={cn(
              'absolute z-50 mt-2 w-full min-w-[200px] rounded-md border bg-white shadow-lg',
              'theme_dropdown_popup_border theme_font theme_dropdown_popup_shadow p-2',
              parentData?.colorSchemeMenuHeader?.background
                ? 'scheme-color-menu-header'
                : 'bg-white',
              data.dropdownWidth === 'AUTO' && 'w-auto',
              data.dropdownWidth === 'MEDIUM' && 'w-80',
              data.dropdownWidth === 'LARGE' && 'w-96'
            )}
            onMouseLeave={() => {
              setOpenMenuId?.('');
              setOpenSubMenuId?.('');
            }}
          >
            <ul className='w-full py-2 text-base'>
              {menu.children?.map((subMenu) => (
                <li key={subMenu.id} className='relative'>
                  <div className='flex cursor-pointer items-center justify-between rounded px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'>
                    <Link
                      href={
                        subMenu.url_path?.includes('http')
                          ? subMenu.url_path
                          : `/${subMenu.url_path}${
                              token
                                ? `?theme_id=${themeId}&token=${token}`
                                : preview
                                ? `?theme_id=${themeId}&isPreview=true`
                                : ''
                            }`
                      }
                      className='text-sm'
                    >
                      {subMenu.title}
                    </Link>
                    {subMenu.children.length > 0 && (
                      <ChevronDownIcon
                        className={`w-4 transition-transform ${
                          openSubMenuId === subMenu.id ? 'rotate-180' : ''
                        }`}
                        onClick={() => {
                          if (openSubMenuId === subMenu.id) {
                            setOpenSubMenuId?.('');
                          } else {
                            setOpenSubMenuId?.(subMenu.id);
                          }
                        }}
                      />
                    )}
                  </div>
                  {openSubMenuId === subMenu.id && subMenu.children.length > 0 && (
                    <ul className='pl-1.5'>
                      {subMenu.children.map((child) => (
                        <li key={child.id}>
                          <Link
                            href={
                              child.url_path.includes('http')
                                ? child.url_path
                                : `/${child.url_path}${
                                    token
                                      ? `?theme_id=${themeId}&token=${token}`
                                      : preview
                                      ? `?theme_id=${themeId}&isPreview=true`
                                      : ''
                                  }`
                            }
                            className='block rounded-sm px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white'
                          >
                            {child.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {openMenuId === menu.id && data.subMenuType === 'MEGA_MENU' && (
          <div
            onMouseLeave={() => setOpenMenuId?.('')}
            data-test-id='header-menu-content-mega-menu'
            className={cn(
              'absolute left-0 top-full z-50 w-screen border bg-white shadow-lg',
              'theme_font flex justify-between p-5',
              parentData?.colorSchemeMenuHeader?.background
                ? 'scheme-color-menu-header'
                : 'bg-white',
              data.megaMenuWidth === 'SMALL' && 'max-w-lg',
              data.megaMenuWidth === 'MEDIUM' && 'max-w-4xl',
              data.megaMenuWidth === 'FULL_WIDTH' && 'w-screen rounded-none'
            )}
          >
            <div className=''>
              <div
                className={cn(
                  menu.children?.some(
                    (menuItem) => menuItem.children && menuItem.children.length > 0
                  )
                    ? 'mega_menu_grid_col grid gap-5'
                    : 'flex flex-col gap-3'
                )}
              >
                {menu.children?.map((menuItem) => (
                  <div key={menuItem?.id}>
                    {!!menuItem.title && (
                      <Link
                        href={
                          menuItem.url_path.includes('http')
                            ? menuItem.url_path
                            : `/${menuItem.url_path}${
                                token
                                  ? `?theme_id=${themeId}&token=${token}`
                                  : preview
                                  ? `?theme_id=${themeId}&isPreview=true`
                                  : ''
                              }`
                        }
                        className={cn(
                          'mb-1 rounded-lg p-2 text-base hover:bg-black/5',
                          menuItem.children.length > 0 ? 'font-semibold' : 'text-xs'
                        )}
                      >
                        {menuItem.title}
                      </Link>
                    )}
                    <ul className={cn('mt-3', menuItem.children.length > 0 && 'pl-2')}>
                      {menuItem?.children?.map((subMenu) => (
                        <li key={subMenu.id}>
                          <Link
                            href={
                              subMenu.url_path?.includes('http')
                                ? subMenu.url_path
                                : `/${subMenu.url_path}${
                                    token
                                      ? `?theme_id=${themeId}&token=${token}`
                                      : preview
                                      ? `?theme_id=${themeId}&isPreview=true`
                                      : ''
                                  }`
                            }
                            className={cn(
                              'flex gap-x-2 rounded-lg p-2 dark:hover:bg-gray-700',
                              parentData?.colorSchemeMenuHeader?.btnSolidlabel
                                ? 'hover-color-scheme'
                                : 'hover:bg-gray-50'
                            )}
                          >
                            <div>
                              <div>{subMenu.title}</div>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className='px-8'>
              <div className='flex gap-2'>
                {menu.images?.slice(0, 3).map((image, index) => (
                  <Link
                    href={
                      image.url_path?.includes('http')
                        ? image.url_path
                        : `/${image.url_path}${
                            token
                              ? `?theme_id=${themeId}&token=${token}`
                              : preview
                              ? `?theme_id=${themeId}&isPreview=true`
                              : ''
                          }`
                    }
                    key={index}
                    passHref={image.url_path?.includes('http')}
                  >
                    <HeaderMenuImage
                      title={image.title}
                      src={image.image_url}
                      parentData={parentData}
                    />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <style jsx global>
        {`
          .popover_full_width {
            width: calc(var(--radix-popover-content-available-width) + 1px);
          }
          .hover-color-scheme:hover {
            background-color: ${parentData?.colorSchemeMenuHeader?.btnSolidlabel};
          }
          .scheme-color-menu-header {
            background-color: ${parentData?.colorSchemeMenuHeader?.background};
            color: ${parentData?.colorSchemeMenuHeader?.text};
          }
          .mega_menu_grid_col {
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          }
        `}
      </style>
    </>
  );
}
